import React, { useState, useContext } from 'react';
import { Container, Typography, TextField, Button, Stack } from '@mui/material';
import './HSIDIDProofing.css';
import Swal from "sweetalert2";
import { TabContext } from '../../home/TabProvider';
import { LoadingButton } from '@mui/lab';

const splitPhoneNumber = (phoneNumber) => {
    const ccode = phoneNumber.length === 11 ? phoneNumber.charAt(0) : '1';
    const phone = phoneNumber.length === 11 ? phoneNumber.slice(1) : phoneNumber;
    return { ccode, phone };
};

const HSIDIDProofing = () => {
    const { sharedData } = useContext(TabContext);
    let { ccode, phone } = splitPhoneNumber(sharedData.eimpData?.phone || '');
    const [phoneNumber, setPhoneNumber] = useState(phone || '');
    const [countryCode, setCountryCode] = useState(ccode || '');
    const [firstName, setFirstName] = useState(sharedData.eimpData?.firstName || '');
    const [lastName, setLastName] = useState(sharedData.eimpData?.lastName || '');
    const [initCompleted, setInitCompleted] = useState(false);
    const [id, setId] = useState('');
    const [otp, setOtp] = useState('');

    const [addressLineOne, setAddressLineOne] = useState(sharedData.eimpData?.address?.addressLineOne || '');
    const [city, setCity] = useState(sharedData.eimpData?.address?.city || '');
    const [provinceState, setProvinceState] = useState(sharedData.eimpData?.address?.provinceState || '');
    const [zipPostalCode, setZipPostalCode] = useState(sharedData.eimpData?.address?.zipPostalCode || '');
    const [dob, setDob] = useState(sharedData.eimpData?.dob || '');

    const [otpValidationProcessing, setOtpValidationProcessing] = useState(false);
    const [associationChecProcessing, setAssociationChecProcessing] = useState(false);

    const handleOtpChange = (event) => {
        setOtp(event.target.value);
    };

    const handleOtpSubmit = () => {
        
        fetch('/api/hsid/verify', {
            method: 'POST',
            body: JSON.stringify({
                id,
                otp
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            if (!response.ok) throw new Error(response.statusText);
            return response.json();
        })
        .then((data) => {
            Swal.fire({
                icon: "success",
                title: "Success",
                text: "Association Check Completed.",
                confirmButtonText: "Cancel"
            })
            .then((result) => {
                if (result.isConfirmed) {
                    setInitCompleted(false);
                }
            });
        })
        .catch((error) => {
            Swal.fire({
                title: "Error",
                text: "OTP validation failed. Please verify the OTP.",
                confirmButtonText: "Cancel"
            })
        });
    };

    const handlePhoneNumberChange = (event) => {
        setPhoneNumber(event.target.value);
    };

    const handleCountryCodeChange = (event) => {
        setCountryCode(event.target.value);
    };

    const validateOTPButtonClick = () => {
        setOtpValidationProcessing(true);
        fetch('/api/hsid/init', {
            method: 'POST',
            body: JSON.stringify({
                phoneNumber,
                countryCode,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            setOtpValidationProcessing(false);
            if (!response.ok) throw new Error(response.statusText);
            return response.json();
        })
        .then((data) => {
            // Handle the response data
            setId(data.id);
            setInitCompleted(true);
        })
        .catch((error) => {
            setOtpValidationProcessing(false);
            console.error(error);
            Swal.fire({
                title: "Error",
                text: "Association Check Failed. Please verify the information.",
                confirmButtonText: "Cancel"
            })
        });
    };
    
    const associationCheckButtonClick = () => {
        setAssociationChecProcessing(true);
        fetch('/api/hsid/association', {
            method: 'POST',
            body: JSON.stringify({
                firstName,
                lastName,
                countryCode,
                phoneNumber,
                dob,
                address: {
                    street_line_one: addressLineOne,
                    city: city,
                    steate: provinceState,
                    zip: zipPostalCode,
                },
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            setAssociationChecProcessing(false);
            if (!response.ok) throw new Error(response.statusText);
            return response.json();
        })
        .then((data) => {
            // Handle the response data 'REJECT'
            if (data.result === 'REJECT') {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Association Check Failed. Please verify the information.",
                    confirmButtonText: "Cancel"
                });
            }
            else {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Association Check Pass.",
                    confirmButtonText: "Cancel"
                })
            }
        })
        .catch((error) => {
            setAssociationChecProcessing(false);
            Swal.fire({
                title: "Error",
                text: "Association Check Failed. Please verify the information.",
                confirmButtonText: "Cancel"
            })
        });
    };

    return (
        <Container className="container">
            <Typography variant="h6" component="h6">
                ID Proofing
            </Typography>
            {initCompleted && (
                <form className="form">
                        <TextField
                            className="textField"
                            label="OTP"
                            value={otp}
                            onChange={handleOtpChange}
                        />
                        <div className="space"></div>
                        <Button
                            className="button"
                            variant="contained"
                            color="primary"
                            onClick={handleOtpSubmit}
                        >
                            Submit OTP
                        </Button>
                    </form>
            )}
            {!initCompleted && (
                <Stack spacing={2}>
                <TextField
                    className="textField"
                    label="First Name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                />
                <TextField
                    className="textField"
                    label="Last Name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                />
                <TextField
                    className="textField"
                    label="Country Code"
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                />
                
                <TextField
                    className="textField"
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                />

                <TextField
                    className="textField"
                    label="Address Line One"
                    value={addressLineOne}
                    onChange={(event) => setAddressLineOne(event.target.value)}
                />
                
                <TextField
                    className="textField"
                    label="City"
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                />
                <TextField
                    className="textField"
                    label="Province/State"
                    value={provinceState}
                    onChange={(event) => setProvinceState(event.target.value)}
                />
                <TextField
                    className="textField"
                    label="Zip/Postal Code"
                    value={zipPostalCode}
                    onChange={(event) => setZipPostalCode(event.target.value)}
                />
                <TextField
                    className="textField"
                    label="Date of Birth"
                    value={dob}
                    onChange={(event) => setDob(event.target.value)}
                />
                <Stack direction="row" spacing={2}>
                    <LoadingButton
                        size="small"
                        loading={otpValidationProcessing} 
                        onClick={validateOTPButtonClick}
                        loadingPosition="center"
                        variant="contained">
                        Validate OTP
                    </LoadingButton>

                    <LoadingButton
                        size="small"
                        loading={associationChecProcessing} 
                        onClick={associationCheckButtonClick}
                        loadingPosition="center"
                        variant="contained">
                        Check Association
                    </LoadingButton>
                </Stack>
            </Stack>
            )}
        </Container>
    );
};

export default HSIDIDProofing;